<template>
  <div :class="$style.journal_search">
    <MoleculesConsumerHeading title="Journal" sub-title="ジャーナル" align="left" />
    <div v-if="state.query && state.totalCount > 0">
      <h2 :class="$style.title">{{ state.query }}の検索結果</h2>
      <p>{{ state.totalCount }}件の記事が見つかりました。</p>
    </div>
    <div v-else-if="state.query" :class="$style.search_title">
      <p>{{ state.query }}の検索結果</p>
      <p>記事が見つかりませんでした。</p>
    </div>
    <JournalList
      v-if="searchJournalsRef"
      :journals="searchJournalsRef"
      :is-search-page="true"
      gtm-class="gtm_article_item"
    />
    <MoleculesConsumerPager :total-page="state.totalPageCount" :current-page="state.currentPage" />
    <JournalOther
      :journal="searchJournalsRef[0] ?? {}"
      :other-journals="otherJournalsRef"
      :category-journals="searchJournalsRef"
    />
  </div>
</template>

<script setup lang="ts">
import { useCustomMeta } from "~/composables/meta"
import { useToast } from "~/state"
import { usePublicJournalAllQuery } from "~/gql/urql.generated"

const route = useRoute()
const toast = useToast()

const customMeta = useCustomMeta()
const siteId = useSite().getSiteId()
const LIMIT = 10

type State = {
  isNextPage: boolean
  searchParams: {
    words: string[]
    limits: number
    pages: number
    status: boolean
  }
  query: string
  count: number
  hasNextPage: boolean
  totalPageCount: number
  currentPage: number
  totalCount: number
}

const state: State = reactive({
  isNextPage: false,
  searchParams: {
    article_type: "journal",
    words: [],
    limits: LIMIT,
    pages: 1,
    status: true,
  },
  query: "" + route.query.searchQuery ?? "",
  count: 0,
  hasNextPage: false,
  totalPageCount: 0,
  currentPage: 1,
  totalCount: 0,
})

const variablesSJ = computed(() => ({
  page: state.currentPage,
  limit: LIMIT,
  site_id: siteId,
  searchText: state.query,
}))

const { data: searchJournals, error: errorSearchJournals } = usePublicJournalAllQuery({
  variables: variablesSJ,
})
watch(
  () => searchJournals.value?.publicJournalAll,
  (value) => {
    if (!value) return
    state.totalCount = value.count ?? 0
    state.totalPageCount = Math.ceil((value.count || 0) / LIMIT)
    state.hasNextPage = value.pageInfo?.hasNextPage || false
    state.count = value.count || 0
  },
  {
    deep: true,
    immediate: true,
  },
)

const { data: otherJournals, error: errorOther } = usePublicJournalAllQuery({
  variables: {
    page: 1,
    limit: 8,
    site_id: siteId,
    filter: { featured_content: true, other_thumbnail_image: "" },
  },
})
const searchJournalsRef = computed(() => searchJournals.value?.publicJournalAll?.items ?? [])
const otherJournalsRef = computed(() => otherJournals.value?.publicJournalAll?.items ?? [])

watch(
  () => errorSearchJournals.value ?? errorOther.value,
  (error) => {
    if (!error) return
    toast?.showErrorToasted(error.message)
  },
  { deep: true },
)

watch(
  () => route.fullPath,
  () => {
    state.currentPage = typeof route.query.page === "string" ? +route.query.page : 1
    state.query = "" + route.query.searchQuery
    window.scrollTo({
      top: 0,
    })
  },
)

useHead(
  customMeta.fetch({
    title: `${state.query}の検索結果`,
    description:
      "TENTIAL JOURNALは、体の不調に対する解決策やコンディショニング方法、挑戦を続けるアスリートへのインタビューなど、あなたに眠っているポテンシャルを引き出すヒントになる情報をお届けします。",
  }),
)
</script>

<style module scoped lang="scss">
.journal_search {
  @include container-lg;
  .title {
    font-size: $font-size-18;
    color: $primary-100;
    margin-bottom: 26px;
    line-height: 1.55;
    @include md {
      font-size: $font-size-26;
      margin-bottom: 30px;
    }
  }
}
</style>
